import { useState, useEffect, useCallback } from 'react';
import {
  UsersRound, Search, Loader2, X, Plus, Trash2, Pencil, AlertTriangle, RefreshCcw,
  Building2, FolderTree,
} from 'lucide-react';
import { toast } from 'sonner';
import { useEnvironment } from '@/contexts/EnvironmentContext';
import {
  listEnterprises, listTeams,
  createTeam, updateTeam, deleteTeam,
  Enterprise, Team,
} from '@/services/organizationApi';

const getErrorMessage = (err: unknown): string => {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: string } } }).response;
    return response?.data?.error || '';
  }
  if (err && typeof err === 'object' && 'message' in err) {
    return (err as { message: string }).message;
  }
  return '';
};

interface FlatTeam extends Team {
  enterprise_name: string;
  parent_name: string;
}

export default function TeamsPage() {
  const { current } = useEnvironment();

  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [allTeams, setAllTeams] = useState<FlatTeam[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [enterpriseFilter, setEnterpriseFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Create dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createEnterpriseId, setCreateEnterpriseId] = useState('');
  const [createParentId, setCreateParentId] = useState('');
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [availableParentTeams, setAvailableParentTeams] = useState<Team[]>([]);

  // Edit dialog state
  const [editDialog, setEditDialog] = useState<FlatTeam | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState<FlatTeam | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const ents = await listEnterprises();
      setEnterprises(ents);

      // Load all teams for each enterprise
      const flatTeams: FlatTeam[] = [];
      for (const ent of ents) {
        const teams = await listTeams(ent.id);
        for (const team of teams) {
          const parent = team.parent_id
            ? teams.find(t => t.id === team.parent_id)
            : null;
          flatTeams.push({
            ...team,
            enterprise_name: ent.name,
            parent_name: parent ? parent.name : '-',
          });
        }
      }
      setAllTeams(flatTeams);
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to load teams');
      setAllTeams([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Load parent teams when enterprise is selected in create dialog
  useEffect(() => {
    if (!createEnterpriseId) {
      setAvailableParentTeams([]);
      setCreateParentId('');
      return;
    }
    listTeams(createEnterpriseId).then(setAvailableParentTeams).catch(() => setAvailableParentTeams([]));
  }, [createEnterpriseId]);

  const filteredTeams = allTeams.filter(team => {
    const matchesSearch = !searchQuery ||
      team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.enterprise_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.parent_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesEnterprise = enterpriseFilter === 'all' || team.enterprise_id === enterpriseFilter;
    return matchesSearch && matchesEnterprise;
  });

  const handleCreate = async () => {
    if (!createEnterpriseId || !createName.trim()) {
      toast.error('Enterprise and name are required');
      return;
    }
    try {
      setActionLoading(true);
      await createTeam({
        enterprise_id: createEnterpriseId,
        parent_id: createParentId || undefined,
        name: createName,
        description: createDescription || undefined,
      });
      toast.success('Team created');
      setShowCreateDialog(false);
      setCreateName('');
      setCreateDescription('');
      setCreateEnterpriseId('');
      setCreateParentId('');
      await loadData();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to create team');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!editDialog || !editName.trim()) return;
    try {
      setActionLoading(true);
      await updateTeam(editDialog.id, { name: editName, description: editDescription || undefined });
      toast.success('Team updated');
      setEditDialog(null);
      await loadData();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to update team');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      setActionLoading(true);
      await deleteTeam(deleteDialog.id);
      toast.success('Team deleted');
      setDeleteDialog(null);
      await loadData();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to delete team');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UsersRound className="w-6 h-6 text-indigo-500" />
          <h2 className="text-xl font-semibold text-gray-800">Teams</h2>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            ({ blue: 'bg-blue-100 text-blue-700', amber: 'bg-amber-100 text-amber-700', red: 'bg-red-100 text-red-700' })[current.color]
          }`}>
            {current.label}
          </span>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          Create Team
        </button>
      </div>

      {current.color === 'red' && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          You are managing the <strong>production</strong> environment.
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search teams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        <select
          value={enterpriseFilter}
          onChange={(e) => setEnterpriseFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="all">All Enterprises</option>
          {enterprises.map(ent => (
            <option key={ent.id} value={ent.id}>{ent.name}</option>
          ))}
        </select>

        <button
          onClick={loadData}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          title="Refresh"
        >
          <RefreshCcw className="w-4 h-4" />
        </button>
      </div>

      {/* Teams table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : filteredTeams.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-12">No teams found</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-3 font-medium text-gray-600">Team</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Enterprise</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Parent</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Description</th>
                <th className="w-24 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredTeams.map(team => (
                <tr key={team.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <UsersRound className="w-4 h-4 text-orange-500" />
                      <span className="font-medium text-gray-900">{team.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <Building2 className="w-3.5 h-3.5 text-indigo-400" />
                      {team.enterprise_name}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <FolderTree className="w-3.5 h-3.5 text-emerald-400" />
                      {team.parent_name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 truncate max-w-[200px]">
                    {team.description || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => { setEditDialog(team); setEditName(team.name); setEditDescription(team.description || ''); }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setDeleteDialog(team)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Team Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Create Team</h3>
              <button onClick={() => setShowCreateDialog(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Enterprise *</label>
                <select
                  value={createEnterpriseId}
                  onChange={(e) => setCreateEnterpriseId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select enterprise</option>
                  {enterprises.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent Team</label>
                <select
                  value={createParentId}
                  onChange={(e) => setCreateParentId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={!createEnterpriseId}
                >
                  <option value="">None (root team)</option>
                  {availableParentTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Team name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={createDescription}
                  onChange={(e) => setCreateDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Optional description"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowCreateDialog(false)}
                disabled={actionLoading}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={actionLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Create Team
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Team Dialog */}
      {editDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Team</h3>
              <button onClick={() => setEditDialog(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setEditDialog(null)}
                disabled={actionLoading}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                disabled={actionLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-2">Delete Team</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{deleteDialog.name}</strong>?
              This is a soft-delete and can be reversed.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteDialog(null)}
                disabled={actionLoading}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
